import {getVideoDimentions} from '../../utils/getVideoDimentions';
import {getImageDimentions} from '../../utils/resizeImage';
import LibraryApi from '../api/LibraryAPI';
import {VideoGenerationConfig} from '../models/AI/VideoGenerationConfig';
import {GeneratedVideoChunk} from '../models/LibraryFile/GeneratedVideoChunk';
import {
  MEDIA_FILES_ALL,
  MEDIA_FILES_DOCUMENT,
  MEDIA_FILES_VIDEO,
  MediaFile,
} from '../models/LibraryFile/MediaFile';
import {ApiResponse} from '../models/ApiResponse';
import {serverErrorCodes} from '../errorCodes';
import {
  AddFileRequest,
  AddFilesResponse,
  UploadFileItem,
} from '../models/LibraryFile/UploadFilesResponse';

// Library file status (same raw values the library UI checks against).
export const FILE_STATUS_READY = 0;
// Uploaded videos are polled until transcoding finishes (READY), since the
// generators that read them validate that status server-side.
const VIDEO_READY_POLL_MS = 5000;
const VIDEO_READY_MAX_POLLS = 60;

export interface SourceUploadResult {
  success: boolean;
  errors?: string[];
  /** Absent when the record could not be found or the upload was cancelled. */
  record?: MediaFile;
}

class LibraryRepository {
  private libraryApi = new LibraryApi();

  /**
   * Uploads a document or video that a generator will read, and returns its
   * library record. Videos are polled until they are READY; on timeout the
   * last record is returned as it is. `isCancelled` stops the polling.
   * `duration` (seconds) and `targetRes` are recorded as with `uploadVideo`;
   * left out, the file is stored with no length at SD. `isReady` replaces the
   * READY check, for callers that need more than the status. `signal` also
   * stops the transfer, so no record is created. With `failWhenGone`, a video
   * the library removes while processing it (it removes one with no sound)
   * fails with `no_audio_in_video` instead of being waited for.
   */
  public async uploadSourceFile(
    organizationId: string,
    file: File,
    {
      isCancelled = () => false,
      signal,
      pollMs = VIDEO_READY_POLL_MS,
      maxPolls = VIDEO_READY_MAX_POLLS,
      duration = 0,
      targetRes = 0,
      isReady = (item: MediaFile) => item.fileStatus === FILE_STATUS_READY,
      failWhenGone = false,
    }: {
      isCancelled?: () => boolean;
      signal?: AbortSignal;
      pollMs?: number;
      maxPolls?: number;
      duration?: number;
      targetRes?: number;
      isReady?: (record: MediaFile) => boolean;
      failWhenGone?: boolean;
    } = {},
  ): Promise<SourceUploadResult> {
    if (isCancelled()) return {success: false};
    const isVideoFile = file.type.startsWith('video');
    const result = await this.uploadVideo(
      organizationId,
      file,
      duration,
      targetRes,
      signal,
    );
    if (!result.success) {
      return {success: false, errors: result.errors};
    }
    // The upload API doesn't return the created record, so fetch the newest
    // library entry matching the file name. Once found, the same record is
    // followed by its id.
    const findRecord = async (
      known?: MediaFile,
    ): Promise<MediaFile | null | undefined> => {
      const listResult = await this.getMediaFiles(
        0,
        organizationId,
        5,
        '',
        isVideoFile ? MEDIA_FILES_VIDEO : MEDIA_FILES_DOCUMENT,
        file.name,
        false,
      );
      if (listResult.success && listResult.data) {
        const files = listResult.data.data as MediaFile[];
        if (!known)
          return files.find(item => item.fileName === file.name) ?? files[0];
        const listed = files.find(item => item.id === known.id);
        if (listed) return listed;
      }
      // The library renames a WebM to .mp4 once it has re-encoded it, so the
      // name no longer finds it. null: the record is gone.
      if (!known) return undefined;
      const byId = await this.getByContentId(organizationId, known.id);
      if (byId.success && byId.data) return byId.data;
      return byId.errors?.includes(serverErrorCodes.ENTITY_NOT_FOUND)
        ? null
        : undefined;
    };

    let record = (await findRecord()) ?? undefined;
    let polls = 0;
    while (
      isVideoFile &&
      record &&
      !isReady(record) &&
      polls < maxPolls &&
      !isCancelled()
    ) {
      await new Promise(resolve => setTimeout(resolve, pollMs));
      const next = await findRecord(record);
      if (next === null && failWhenGone)
        return {success: false, errors: [serverErrorCodes.NO_AUDIO_IN_VIDEO]};
      record = next ?? record;
      polls++;
    }
    if (isCancelled()) {
      return {success: false, errors: result.errors};
    }
    return {success: true, errors: result.errors, record};
  }

  public async uploadVideo(
    organizationId: string,
    file: File,
    duration: number,
    targetRes: number,
    signal?: AbortSignal,
  ) {
    const urlResult = await this.libraryApi.generateVideoUploadLink(
      organizationId,
      file.type,
      Math.ceil(file.size / 1024 / 1024),
    );
    if (!urlResult.success || !urlResult.data) {
      return urlResult;
    }
    try {
      const uploadResult = await this.libraryApi.uploadFileToStorage(
        file,
        urlResult.data.uploadUrl,
        signal,
      );
      // A stopped upload creates no record.
      if (signal?.aborted) throw new Error('The upload was cancelled.');
      if (uploadResult.ok) {
        if (file.type.includes('image')) {
          const dimentions = await getImageDimentions(file);
          return await this.libraryApi.createMediaLibraryFile(
            organizationId,
            urlResult.data.storageFileName,
            file.type,
            file.name,
            Math.ceil(file.size / 1024 / 1024),
            duration,
            dimentions?.width || 0,
            dimentions?.height || 0,
            null,
          );
        } else if (file.type.includes('video')) {
          const dimentions = await getVideoDimentions(file);
          return await this.libraryApi.createMediaLibraryFile(
            organizationId,
            urlResult.data.storageFileName,
            file.type,
            file.name,
            Math.ceil(file.size / 1024 / 1024),
            duration,
            dimentions?.width || 0,
            dimentions?.height || 0,
            targetRes,
          );
        } else {
          return await this.libraryApi.createMediaLibraryFile(
            organizationId,
            urlResult.data.storageFileName,
            file.type,
            file.name,
            Math.ceil(file.size / 1024 / 1024),
            duration,
            0,
            0,
            null,
          );
        }
      }
    } catch {
      return Promise.resolve({
        success: false,
        errors: ['unknown'],
      } as ApiResponse<MediaFile>);
    }

    return urlResult;
  }

  // The whole set goes up at once: the storage quota is checked for all of it
  // before anything is transferred and the records are created in one call.
  public async uploadFiles(
    organizationId: string,
    files: UploadFileItem[],
  ): Promise<ApiResponse<AddFilesResponse>> {
    const items = await Promise.all(
      files.map(async ({file, duration, targetRes}, index) => {
        const isVideo = file.type.includes('video');
        const dimentions = file.type.includes('image')
          ? await getImageDimentions(file)
          : isVideo
          ? await getVideoDimentions(file)
          : undefined;
        return {
          id: `${index}`,
          file,
          type: file.type,
          name: file.name,
          fileSizeInMb: Math.ceil(file.size / 1024 / 1024),
          duration: duration ?? 0,
          width: dimentions?.width || 0,
          height: dimentions?.height || 0,
          videoResolution: isVideo ? targetRes ?? null : null,
        };
      }),
    );

    const urlResult = await this.libraryApi.generateFilesUploadLinks(
      organizationId,
      items.map(item => ({
        id: item.id,
        type: item.type,
        fileSizeInMb: item.fileSizeInMb,
      })),
    );
    if (!urlResult.success || !urlResult.data) {
      return {success: false, errors: urlResult.errors};
    }

    const slots = new Map(urlResult.data.files.map(slot => [slot.id, slot]));

    try {
      const added: AddFileRequest[] = [];
      const uploads = await Promise.all(
        items.map(async item => {
          const slot = slots.get(item.id);
          if (!slot) return undefined;
          const uploadResult = await this.libraryApi.uploadFileToStorage(
            item.file,
            slot.uploadUrl,
          );
          if (!uploadResult.ok) return undefined;
          return {
            id: item.id,
            storageFileName: slot.storageFileName,
            type: item.type,
            name: item.name,
            fileSizeInMb: item.fileSizeInMb,
            duration: item.duration,
            width: item.width,
            height: item.height,
            videoResolution: item.videoResolution,
          };
        }),
      );
      uploads.forEach(item => item && added.push(item));

      if (!added.length) {
        return {success: false, errors: ['unknown']};
      }

      return await this.libraryApi.createMediaLibraryFiles(
        organizationId,
        added,
      );
    } catch {
      return {success: false, errors: ['unknown']};
    }
  }

  public getMediaFiles(
    page: number,
    organizationId: string,
    pageSize: number,
    orderBy: string,
    typeFilter?: number,
    search?: string,
    orderAsk?: boolean,
    fileStatusFilter?: number,
  ) {
    return this.libraryApi.getLibraryFiles(
      page,
      organizationId,
      pageSize,
      orderBy,
      typeFilter !== undefined ? typeFilter : MEDIA_FILES_ALL,
      search,
      orderAsk,
      fileStatusFilter,
    );
  }

  public getByContentId(organizationId: string, contentId: string) {
    return this.libraryApi.getFileByContentId(organizationId, contentId);
  }

  public deleteMediaFile(organizationId: string, mediaFileId: string) {
    return this.libraryApi.deleteMediaFile(organizationId, mediaFileId);
  }

  public downloadTranscript(organizationId: string, mediaFileId: string) {
    return this.libraryApi.downloadTranscript(organizationId, mediaFileId);
  }

  public async getVimeoVideos(
    organizationId: string,
    page: number,
    pageSize: number,
  ) {
    return this.libraryApi.getVimeoVideos(organizationId, page, pageSize);
  }

  public async generateAiVideosFromSlides(
    organizationId: string,
    config: VideoGenerationConfig,
    assistantAvatar?: File,
  ) {
    const result = await this.libraryApi.generateAiVideosFromSlides(
      organizationId,
      config,
      assistantAvatar,
    );
    return result;
  }

  public async getFileOrientation(organizationId: string, fileId: string) {
    return this.libraryApi.getFileOrientation(organizationId, fileId);
  }

  public getVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
  ) {
    return this.libraryApi.getVideoChunksForGeneratedContent(
      organizationId,
      mediaFileId,
    );
  }

  public async updateVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
    chunks: GeneratedVideoChunk[],
  ) {
    return this.libraryApi.updateVideoChunksForGeneratedContent(
      organizationId,
      mediaFileId,
      chunks,
    );
  }

  public async compileVideoFile(organizationId: string, mediaFileId: string) {
    return this.libraryApi.compileVideoFile(organizationId, mediaFileId);
  }

  public async editChunk(
    organizationId: string,
    mediaFileId: string,
    option: EditChunkOption,
    chunkId: string,
    additionalParameters: Record<string, any> = {},
  ) {
    return this.libraryApi.editChunk(
      organizationId,
      mediaFileId,
      option,
      chunkId,
      additionalParameters,
    );
  }

  public async addChunk(
    organizationId: string,
    mediaFileId: string,
    payload: {
      libraryMediaFileId?: string;
      storyBlocksVideoId?: number;
      start: number;
      stop: number;
      mute: boolean;
    },
  ) {
    return this.libraryApi.addChunk(organizationId, mediaFileId, payload);
  }

  public async saveEdit(organizationId: string, mediaFileId: string) {
    return this.libraryApi.saveEdit(organizationId, mediaFileId);
  }
  public async cancelEdit(organizationId: string, mediaFileId: string) {
    return this.libraryApi.cancelEdit(organizationId, mediaFileId);
  }

  public async cancelGenerationAndDelete(
    organizationId: string,
    mediaFileId: string,
  ) {
    return this.libraryApi.cancelGenerationAndDelete(
      organizationId,
      mediaFileId,
    );
  }

  public async getStoryblocksMedia(
    organizationId: string,
    chunkId: string | null | undefined,
    page: number,
    pageSize: number,
    search: string,
  ) {
    return this.libraryApi.getStoryblocksMedia(
      organizationId,
      chunkId,
      page,
      pageSize,
      search,
    );
  }
  public async changeChunkToStoryblocksMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    storyBlocksVideoId: string,
  ) {
    return this.editChunk(
      organizationId,
      mediaFileId,
      EditChunkOption.ChangeMedia,
      chunkId,
      {storyBlocksVideoId: storyBlocksVideoId},
    );
  }
  public async changeChunkToLibraryMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string, // AiVideoChunkId
    libraryMediaId: string, // LibraryMediaId
    isImage: boolean, // true -> Option 6, false -> Option 5
  ) {
    // ⚠️ Importante: NO usar editChunk genérico aquí (usa minúsculas).
    // Llamamos al método específico del API que respeta el casing exacto.
    return this.libraryApi.replaceChunkWithLibraryMedia(
      organizationId,
      mediaFileId,
      chunkId,
      libraryMediaId,
      isImage,
    );
  }

  public async getChunkNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
  ) {
    return this.libraryApi.getChunkNarration(
      organizationId,
      mediaFileId,
      chunkId,
    );
  }

  public async updateChunkNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    text: string,
  ) {
    return this.libraryApi.updateChunkNarration(
      organizationId,
      mediaFileId,
      chunkId,
      text,
    );
  }
  public async replaceAIMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    replacements: {artifactUri: string; libraryMediaFileId: string}[],
  ) {
    return this.libraryApi.replaceAIMedia(
      organizationId,
      mediaFileId,
      chunkId,
      replacements,
    );
  }

  public async updateChunkArtifacts(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    artifacts: {
      artifactId: string | null;
      libraryMediaFileId: string | null;
      storyBlocksVideoId: number | null;
      order: number;
      startTime: number;
      duration: number;
    }[],
  ) {
    return this.libraryApi.updateChunkArtifacts(
      organizationId,
      mediaFileId,
      chunkId,
      artifacts,
    );
  }
}

export enum EditChunkOption {
  MuteAudio = 0,
  Regenerate = 1,
  Delete = 2,
  ChangeMedia = 3,
  Reorder = 4,
  ChangeFromLibrary = 5,
  // ChangeFromLibraryImage = 6 on backend
  EditNarration = 7,
  EditScript = -1, // not implemented on backend yet
  ReplaceAIMedia = 8,
  ChangeBackgroundAudio = 9,
  DisableBackgroundAudio = 10,
  EditMedia = 11,
}
export default LibraryRepository;
